import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  HiOutlineRocketLaunch, HiOutlineShieldCheck, HiOutlineChartBar,
  HiOutlineCog6Tooth, HiOutlineLockClosed, HiOutlineMap, HiOutlineCpuChip,
  HiOutlineClipboardDocumentCheck, HiOutlineArrowRight, HiOutlineMagnifyingGlass,
  HiOutlinePencil, HiOutlineWrench, HiOutlineBolt, HiOutlineUserGroup,
  HiOutlineClock,
} from 'react-icons/hi2';
import { TbTargetArrow, TbDatabase, TbRobot, TbShieldLock } from 'react-icons/tb';


/* ── Count-up hook ── */
const useCountUp = (target, duration = 2000) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const startTime = performance.now();
          const step = (now) => {
            const progress = Math.min((now - startTime) / duration, 1);
            // ease-out cubic
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * target));
            if (progress < 1) requestAnimationFrame(step);
            else setCount(target);
          };
          requestAnimationFrame(step);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);

  return { count, ref };
};

/* ── Individual animated stat ── */
const AnimatedStat = ({ value, suffix, label, delay = 0 }) => {
  const { count, ref } = useCountUp(value, 1800 + delay);
  return (
    <div className="stat-item" ref={ref}>
      <div className="stat-number">
        {count}{suffix}
      </div>
      <div className="stat-label">{label}</div>
    </div>
  );
};

/* ── Marquee items ── */
const MARQUEE_ITEMS = [
  'Snowflake Cortex AI', 'ServiceNow Migration', 'Delta Lake', 'Apache Iceberg',
  'Databricks', 'MLOps', 'GenAI & RAG', 'Lakehouse Architecture', 'Fivetran',
  'dbt Cloud', 'AWS Bedrock', 'Azure ML', 'HPSM Knowledge Migration',
  'Virtual Agent AI', 'CDC Streaming',
];

/* ── Accordion layer data ── */
const LAYERS = [
  {
    id: 'l1', label: 'L1', title: 'Foundation', count: '3 services',
    items: [
      { name: 'AI Strategy & Advisory', sub: 'Maturity assessment, use-case prioritization, ROI modeling, governance frameworks, executive alignment' },
      { name: 'Data Engineering & Architecture', sub: 'Lakehouse design, Delta Lake / Iceberg build, ELT pipelines, Fivetran, dbt Cloud, streaming ingestion' },
      { name: 'Data Governance & Quality', sub: 'Data lineage, cataloging, quality rules, access controls, regulatory alignment (FDA, HIPAA, SOC 2)' },
    ],
    highlight: false,
  },
  {
    id: 'l2', label: 'L2', title: 'AI Accelerators', count: '3 accelerators',
    items: [
      { name: 'GenAI / RAG Applications', sub: 'Build & deploy GenAI solutions, customized to your enterprise workflows and data.' },
      { name: 'Data Platform Modernization', sub: 'Cost-effective, scalable data platform modernization, optimizing your existing data.' },
      { name: 'MLOps & Governance', sub: 'Set the AI and ML enablement roadmap with full governance and compliance.' },
    ],
    highlight: true, defaultOpen: true,
  },
  {
    id: 'l3', label: 'L3', title: 'Build & Deploy', count: '6 services',
    items: [
      { name: 'Machine Learning & Predictive AI', sub: 'Demand forecasting, anomaly detection, quality prediction, yield optimization, churn/risk models' },
      { name: 'GenAI & Large Language Models', sub: 'RAG pipelines, fine-tuning, prompt engineering, document intelligence, LLM orchestration' },
      { name: 'AI Agents & Automation', sub: 'Autonomous agents executing multi-step enterprise workflows connected to your live data' },
      { name: 'MLOps & Model Lifecycle', sub: 'Model registry, CI/CD pipelines, monitoring, drift detection, retraining, cost optimization' },
      { name: 'AI Applications & Integrations', sub: 'Dashboards, conversational interfaces, embedded analytics, workflow automation tools' },
      { name: 'Compliance by Design', sub: 'Explainability layers, bias auditing, regulatory frameworks baked in from day one' },
    ],
    highlight: false,
  },
  {
    id: 'l4', label: 'L4', title: 'Sustain & Scale', count: '2 services',
    items: [
      { name: 'Training & Change Management', sub: 'AI literacy programs, executive workshops, frontline enablement, organizational change management' },
      { name: 'Managed Services & Continuous Improvement', sub: 'Ongoing model monitoring, retraining, platform upgrades, cost optimization, new AI use cases' },
    ],
    highlight: false,
  },
];

/* ── Accordion component ── */
const LayerAccordion = () => {
  const [open, setOpen] = useState({ l2: true });

  const toggle = (id, isHighlight) => {
    if (isHighlight) return; // highlighted layer is always open
    setOpen(prev => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="layer-accordion">
      {LAYERS.map((layer) => {
        const isOpen = layer.highlight || open[layer.id];
        return (
          <div
            key={layer.id}
            className={`layer-item ${isOpen ? 'open' : ''} ${layer.highlight ? 'highlight' : ''}`}
          >
            <div
              className="layer-header"
              onClick={() => toggle(layer.id, layer.highlight)}
            >
              <span className="layer-num">{layer.label}</span>
              <span className="layer-title">{layer.title}</span>
              <span className="layer-count">{layer.count}</span>
              {!layer.highlight && (
                <span className="layer-arrow">{isOpen ? '▴' : '▾'}</span>
              )}
            </div>
            {isOpen && (
              <div className="layer-body">
                {layer.items.map((item, i) => (
                  <div className="layer-item-cell" key={i}>
                    <div className="layer-item-name">{item.name}</div>
                    <div className="layer-item-sub">{item.sub}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ── Main HomePage ── */
const HomePage = () => {
  const [emailVal, setEmailVal] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState(false);

  const handleEmailSend = () => {
    if (!emailVal.includes('@')) {
      setEmailError(true);
      setTimeout(() => setEmailError(false), 1500);
      return;
    }
    setEmailSent(true);
    setTimeout(() => { setEmailSent(false); setEmailVal(''); }, 3000);
  };

  return (
    <>
      {/* ── HERO ── */}
      <section className="hero" id="home">
        <div className="hero-grid-bg"></div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="hero-badge">
                <HiOutlineRocketLaunch style={{ fontSize: 16 }} /> Full-Stack AI Enablement
              </div>
              <h1>
                Turn your data into <span className="accent">production-ready</span>{' '}
                <span className="accent-amber">AI.</span>
              </h1>
              <p>
                Disen Systems enables secure GenAI and machine learning adoption—modernizing
                data platforms, deploying copilots, and governing models at scale.
              </p>
              <div className="hero-buttons">
                <Link to="/contact" className="btn-primary">
                  Book a Discovery Call <HiOutlineArrowRight />
                </Link>
                <Link to="/case-studies" className="btn-secondary">
                  View Case Studies
                </Link>
              </div>
              <div className="hero-trust">
                <div className="trust-item">
                  <span className="trust-icon"><HiOutlineLockClosed /></span> Security-first Mindset
                </div>
                <div className="trust-item">
                  <span className="trust-icon"><HiOutlineChartBar /></span> Measurable ROI
                </div>
                <div className="trust-item">
                  <span className="trust-icon"><HiOutlineCog6Tooth /></span> Production Grade
                </div>
              </div>
            </div>
            <div className="hero-image-wrapper">
              <img
                src="/images/hero-modern.png"
                alt="Modern Enterprise AI Data Center & Governance Dashboard"
              />
            </div>
          </div>


        </div>
      </section>

      {/* ── MARQUEE ── */}
      <div className="marquee-wrap">
        <div className="marquee-track">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <div className="marquee-item" key={i}>{item}</div>
          ))}
        </div>
      </div>

      {/* ── STATS STRIP ── */}
      <div className="stats-strip">
        <div className="container">
          <div className="stats-grid">
            <AnimatedStat value={40}  suffix="%" label="Faster Reporting"       delay={0}   />
            <AnimatedStat value={60}  suffix="%" label="Search Time Reduction"  delay={150} />
            <AnimatedStat value={99}  suffix="%" label="Reconciliation Accuracy" delay={300} />
            <AnimatedStat value={3}   suffix="×" label="AI ROI Delivered"       delay={450} />
          </div>
        </div>
      </div>

      {/* ── WHAT WE ENABLE ── */}
      <section className="section section-darker">
        <div className="container">
          <div className="section-header">
            <div className="section-label amber">Capabilities</div>
            <h2>What we enable</h2>
          </div>
          <div className="services-grid">
            {[
              { icon: <TbTargetArrow />, title: 'AI Strategy & Readiness', desc: 'Set the AI and ML enablement roadmap for your enterprise, past technology.' },
              { icon: <TbDatabase />, title: 'Data Foundation for AI', desc: 'Cost-effective, scalable data platform modernization, optimizing your existing data.' },
              { icon: <TbRobot />, title: 'GenAI / RAG Applications', desc: 'Build & deploy GenAI solutions, customized to your enterprise workflows and data.' },
              { icon: <TbShieldLock />, title: 'MLOps & Governance', desc: 'Set the AI and ML enablement roadmap for your enterprise with governance.' },
            ].map((service, i) => (
              <div className={`service-card fade-in-up-delay${i + 1}`} key={i}>
                <div className="service-icon">{service.icon}</div>
                <h3>{service.title}</h3>
                <p>{service.desc}</p>
                <Link to="/solutions" className="learn-more">Learn More <HiOutlineArrowRight /></Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES ACCORDION (new from reference) ── */}
      <section className="section section-dark">
        <div className="container">
          <div className="services-intro-grid">
            <div>
              <div className="section-label">13 Core Services</div>
              <h2 style={{ fontSize: 42, fontWeight: 800, letterSpacing: '-0.5px', marginTop: 12 }}>
                One partner<br />across every layer.
              </h2>
            </div>
            <div>
              <p style={{ color: 'var(--text-secondary)', fontSize: 16, lineHeight: 1.8, marginBottom: 20 }}>
                We operate across <strong style={{ color: 'var(--text-primary)' }}>4 delivery layers</strong>. Every engagement starts at the right layer for your current maturity. Click any layer to explore.
              </p>
              <div className="layer-legend">
                {[
                  { color: 'var(--text-primary)', label: 'L1 · Foundation — strategy, data, governance' },
                  { color: 'var(--cyan)', label: 'L2 · AI Accelerators — pre-built fast-deploy' },
                  { color: '#818cf8', label: 'L3 · Build & Deploy — models, apps, integrations' },
                  { color: '#34d399', label: 'L4 · Sustain & Scale — training, managed services' },
                ].map((leg, i) => (
                  <div className="legend-item" key={i}>
                    <span className="legend-dot" style={{ background: leg.color }}></span>
                    {leg.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <LayerAccordion />
        </div>
      </section>

      {/* ── PROCESS STEPS ── */}
      <section className="section section-darker" style={{ paddingTop: 0, paddingBottom: 60 }}>
        <div className="container">
          <div className="process-steps">
            {[
              { label: 'Assess', icon: <HiOutlineMagnifyingGlass /> },
              { label: 'Design', icon: <HiOutlinePencil /> },
              { label: 'Build', icon: <HiOutlineWrench /> },
              { label: 'Deploy', icon: <HiOutlineRocketLaunch /> },
              { label: 'Govern', icon: <HiOutlineShieldCheck /> },
            ].map((step, i) => (
              <React.Fragment key={step.label}>
                {i > 0 && <span className="step-arrow"><HiOutlineArrowRight /></span>}
                <div className="process-step">
                  <span className="step-icon">{step.icon}</span>
                  <span>{step.label}</span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      </section>

      {/* ── CASE STUDIES ── */}
      <section className="section section-dark">
        <div className="container">
          <div className="section-header" style={{ textAlign: 'left' }}>
            <div className="section-label amber">Success Stories</div>
            <h2>Case Studies</h2>
          </div>
          <div className="case-studies-grid">
            {[
              { img: '/images/case-study-reporting.png', badge: 'Finance', stat: '40', unit: '%', title: 'Faster Reporting', desc: 'Global, $14 bn alternative asset management' },
              { img: '/images/case-study-search.png', badge: 'Technology', stat: '60', unit: '%', title: 'Search Time Reduction', desc: 'Market-focused planner, time optimization' },
              { img: '/images/case-study-reconciliation.png', badge: 'Banking', stat: '99.0', unit: '%', title: 'Reconciliation Accuracy', desc: 'Global banking, process automation' },
            ].map((cs, i) => (
              <div className={`case-card fade-in-up-delay${i + 1}`} key={i}>
                <img src={cs.img} alt={cs.title} className="case-card-image" />
                <div className="case-card-content">
                  <div className="case-badge">{cs.badge}</div>
                  <div className="case-stat">
                    {cs.stat}<span className="stat-highlight">{cs.unit}</span>
                  </div>
                  <h3>{cs.title}</h3>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{cs.desc}</p>
                  <Link to="/case-studies" className="learn-more" style={{ marginTop: 12, display: 'inline-flex' }}>
                    Read More <HiOutlineArrowRight />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OUR SOLUTIONS ── */}
      <section className="section section-darker">
        <div className="container">
          <div className="section-header" style={{ textAlign: 'left' }}>
            <div className="section-label amber">Expertise</div>
            <h2>Our Solutions</h2>
          </div>
          <div className="solutions-grid">
            {[
              { icon: <HiOutlineMap />, title: 'AI Roadmap & Strategy', desc: 'Define your AI vision, identifying cost drivers and best practices.' },
              { icon: <TbDatabase />, title: 'Data Platform Modernization', desc: 'Streamlined data infrastructure, centralizing data and optimizing pipelines.' },
              { icon: <HiOutlineCpuChip />, title: 'GenAI / RAG Applications', desc: 'Build AI-driven solutions from production-ready models and enterprise workflows.' },
              { icon: <HiOutlineClipboardDocumentCheck />, title: 'LLM Evaluation', desc: 'Benchmark AI models to ensure performance, reliability, and cost-efficiency.' },
            ].map((sol, i) => (
              <div className={`solution-card fade-in-up-delay${i + 1}`} key={i}>
                <div className="solution-icon">{sol.icon}</div>
                <h3>{sol.title}</h3>
                <p>{sol.desc}</p>
                <Link to="/solutions" className="learn-more">Read More <HiOutlineArrowRight /></Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY US (new from reference) ── */}
      <section className="section section-dark">
        <div className="container">
          <div className="why-grid">
            <div>
              <div className="section-label">Why Disen Systems</div>
              <h2 style={{ fontSize: 42, fontWeight: 800, letterSpacing: '-0.5px', marginTop: 12, marginBottom: 16 }}>
                AI that ships,<br />not slide decks.
              </h2>
              <p style={{ color: 'var(--text-secondary)', fontSize: 16, lineHeight: 1.8, marginBottom: 28 }}>
                Most AI projects fail not because the technology doesn't work, but because the implementation layer is weak. We close that gap.
              </p>
              <div className="why-metrics">
                {[
                  { value: '6–8 wks', label: 'Time to production' },
                  { value: '13', label: 'Core AI service lines' },
                  { value: 'Zero', label: 'Rip-and-replace needed' },
                  { value: '4', label: 'Delivery layers covered' },
                ].map((m, i) => (
                  <div className="why-metric-item" key={i}>
                    <div className="why-metric-value">{m.value}</div>
                    <div className="why-metric-label">{m.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="why-points">
              {[
                {
                  icon: <HiOutlineBolt />, color: 'rgba(34,211,238,0.1)', stroke: 'var(--cyan)',
                  title: 'Accelerators over custom builds',
                  desc: 'Pre-built frameworks compress months of work into weeks — lower risk, lower cost, proven delivery patterns.',
                },
                {
                  icon: <HiOutlineShieldCheck />, color: 'rgba(52,211,153,0.1)', stroke: '#34d399',
                  title: 'Additive, not disruptive',
                  desc: 'We build AI on top of Oracle, SQL Server, and your existing investments. No need to abandon what works.',
                },
                {
                  icon: <HiOutlineUserGroup />, color: 'rgba(129,140,248,0.1)', stroke: '#818cf8',
                  title: 'One partner, full stack',
                  desc: 'Strategy, data, models, GenAI, MLOps, integration, applications, training — all under one roof.',
                },
                {
                  icon: <HiOutlineClock />, color: 'rgba(251,191,36,0.1)', stroke: '#fbbf24',
                  title: 'We build your internal capability',
                  desc: 'Every engagement includes knowledge transfer. Genuine partnership, not dependency.',
                },
              ].map((pt, i) => (
                <div className="why-point" key={i}>
                  <div className="why-point-icon" style={{ background: pt.color, color: pt.stroke }}>
                    {pt.icon}
                  </div>
                  <div>
                    <div className="why-point-title">{pt.title}</div>
                    <p className="why-point-desc">{pt.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── ARCHITECTURE + TESTIMONIALS ── */}
      <section className="section section-darker">
        <div className="container">
          <div className="architecture-section">
            <div className="arch-image">
              <img src="/images/enterprise-architecture.png" alt="Enterprise AI Architecture" />
            </div>
            <div>
              <div className="section-label">Enterprise AI</div>
              <h2 style={{ fontSize: 32, fontWeight: 700, marginBottom: 32, marginTop: 12 }}>
                Enterprise <span style={{ color: 'var(--cyan)' }}>AI Enablement</span> Architecture
              </h2>
              <ul className="arch-features">
                {[
                  'Secure Ingestion',
                  'Knowledge Grounding',
                  'Vector Search',
                  'Continuous Evaluation',
                  'Observability & Monitoring',
                  'CI / CD Integration',
                ].map((feat) => (
                  <li key={feat}>
                    <span className="arch-bullet"></span>
                    {feat}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA with inline email form (new from reference) ── */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to operationalize <span>AI</span> in weeks?</h2>
            <p>Tell us about your challenge — we respond within one business day.</p>

            {/* Inline email form – from reference code */}
            <div className={`cta-email-form ${emailError ? 'error' : ''}`}>
              <input
                type="email"
                placeholder="your@company.com"
                value={emailVal}
                onChange={e => setEmailVal(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleEmailSend()}
              />
              <button
                onClick={handleEmailSend}
                style={emailSent ? { background: '#34d399' } : {}}
              >
                {emailSent ? '✓ Sent' : 'Get in touch →'}
              </button>
            </div>

            <div className="cta-buttons" style={{ marginTop: 24 }}>
              <Link to="/contact" className="btn-primary">Book a Discovery Call <HiOutlineArrowRight /></Link>
              <Link to="/contact" className="btn-secondary">Contact Us</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
